import { getAuth } from 'firebase/auth';
import logger from '../../utils/logger';

interface RequestOptions extends RequestInit {
  requiresAuth?: boolean;
  params?: Record<string, string | number | boolean | undefined | null>;
  timeout?: number; // Timeout in milliseconds (default: 30000)
}

class ApiClient {
  private baseUrl: string;
  private defaultHeaders: HeadersInit;

  constructor() {
    this.baseUrl = process.env.REACT_APP_API_URL || '';
    
    // Validate API URL configuration
    if (!this.baseUrl) {
      const errorMsg = 'API URL is not configured. Please set REACT_APP_API_URL environment variable.';
      if (process.env.NODE_ENV === 'development') {
        logger.error(errorMsg);
        this.baseUrl = 'http://localhost:8000'; // Only use localhost in development
      } else {
        throw new Error(errorMsg);
      }
    }
    
    // Only log in development
    if (process.env.NODE_ENV === 'development') {
      logger.info(`API Client configured with base URL: ${this.baseUrl}`);
    }
    
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    };
  }

  /**
   * Get the authentication token from Firebase
   * Enhanced with force refresh capability for 401 handling
   */
  private async getAuthToken(forceRefresh = false): Promise<string> {
    const auth = getAuth();
    const user = auth.currentUser;
    
    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('User not authenticated');
    }
    
    try {
      // Get token with optional force refresh for 401 retry scenarios
      // Firebase automatically handles refresh when tokens are close to expiry
      // This provides seamless social app experience with no interruptions
      const token = await user.getIdToken(forceRefresh);
      
      if (forceRefresh) {
        logger.info('✅ Successfully refreshed auth token for seamless social app experience');
      }
      
      return token;
    } catch (error) {
      logger.error('Error getting auth token:', error);
      throw new Error('Failed to get authentication token');
    }
  }

  /**
   * Build the full URL with query parameters
   */
  private buildUrl(endpoint: string, params?: Record<string, string | number | boolean | undefined | null>): string {
    const url = new URL(`${this.baseUrl}${endpoint}`);
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          url.searchParams.append(key, value.toString());
        }
      });
    }
    
    return url.toString();
  }

  /**
   * Make a request to the API
   * Enhanced with automatic token refresh on 401 errors
   */
  private async request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.makeRequestWithRetry<T>(endpoint, options, false);
  }

  /**
   * Internal method to make requests with automatic 401 retry logic
   */
  private async makeRequestWithRetry<T>(endpoint: string, options: RequestOptions = {}, isRetry = false): Promise<T> {
    const { requiresAuth = true, params, timeout = 30000, _isFormData, ...fetchOptions } = options as any;
    const isPrivateReporting = endpoint.startsWith('/api/clearmat/reporting/');

    // Prepare headers — skip Content-Type for FormData so the browser sets it with boundary
    const headers = new Headers(this.defaultHeaders);
    if (_isFormData) {
      headers.delete('Content-Type');
    }
    
    // Add authorization token if required
    if (requiresAuth) {
      try {
        // On retry, force refresh the token; otherwise use cached token
        const token = await this.getAuthToken(isRetry);
        headers.append('Authorization', `Bearer ${token}`);
      } catch (error) {
        logger.error('Failed to get auth token:', error);
        throw new Error('Authentication required');
      }
    }
    
    // Build URL with query parameters
    const url = this.buildUrl(endpoint, params);

    // Setup abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
      logger.warn(`Request to ${endpoint} timed out after ${timeout}ms`);
    }, timeout);

    try {
      // Make a simple CORS request without credentials
      const response = await fetch(url, {
        ...fetchOptions,
        headers,
        mode: 'cors',
        credentials: 'omit', // Never send credentials for cross-origin requests
        signal: controller.signal,
      });

      // Clear timeout on successful response
      clearTimeout(timeoutId);
      
      // Handle HTTP errors
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'No error details available');
        
        // Handle 401 Unauthorized with automatic token refresh
        if (response.status === 401 && requiresAuth && !isRetry) {
          logger.warn('🔄 Received 401 error, attempting token refresh and retry...');
          
          try {
            // Retry the request with token refresh
            return await this.makeRequestWithRetry<T>(endpoint, options, true);
          } catch (retryError) {
            logger.error('❌ Token refresh retry failed:', isPrivateReporting ? '[private reporting details omitted]' : retryError);
            // Fall through to normal error handling
          }
        }
        
        // Log detailed error information
        logger.error(`API Error (${response.status})`, {
          url: isPrivateReporting ? endpoint : url,
          statusCode: response.status,
          statusText: response.statusText,
          errorDetails: isPrivateReporting ? '[private reporting details omitted]' : errorText,
          endpoint,
          params: isPrivateReporting ? undefined : params,
          isRetryAttempt: isRetry
        });
        
        // Throw error with more details. `status` is attached so callers can
        // branch on HTTP codes — several pages checked the axios-style
        // `err?.response?.status`, which never existed on this plain Error,
        // leaving their 403/409/429-specific messages unreachable.
        const apiError = new Error(`API Error: ${response.status} ${response.statusText}${errorText ? ` - ${errorText}` : ''}`) as Error & { status?: number };
        apiError.status = response.status;
        throw apiError;
      }
      
      // Try to parse JSON response
      try {
        const contentType = response.headers.get('content-type');
        
        // Check if response is JSON
        if (contentType && contentType.includes('application/json')) {
          const data = await response.json();
          
          // Special handling for search endpoints - ensure consistent uid/id fields
          if (endpoint.includes('/search') && data) {
            // Process search results to ensure consistent uid/id fields
            if (data.results && Array.isArray(data.results)) {
              data.results = data.results.map((result: any, index: number) => {
                // If this is a user search result, ensure both uid and id fields are present
                if (result) {
                  // Get raw values
                  const rawId = result.id;
                  const rawUid = result.uid;
                  const resultId = rawId || rawUid || '';
                  
                  // Validate ID format and create a safe ID if needed
                  const isValidId = typeof resultId === 'string' && resultId.length > 0;
                  const validId = isValidId ? resultId : `user-${Date.now()}-${index}-${Math.random().toString(36).substring(2, 10)}`;
                  
                  // Create a consistent object format with both id and uid fields
                  return {
                    ...result,
                    // Ensure both id and uid fields are present with the same value
                    id: validId,
                    uid: validId,
                    // Add metadata about the ID resolution
                    _idResolved: true,
                    _idSource: rawId ? 'id' : (rawUid ? 'uid' : 'generated')
                  };
                }
                return result;
              });
            }
          }
          
          return data as T;
        } else {
          const textResponse = await response.text();
          if (isPrivateReporting) throw new Error('Expected a private reporting JSON response');
          logger.warn(`Non-JSON response from ${endpoint}:`, textResponse);
          
          // For search endpoints, return a structured empty results object
          if (endpoint.includes('/search')) {
            return { 
              results: [], 
              total: 0, 
              took_ms: 0, 
              message: 'Search response was not JSON format'
            } as T;
          }
          
          // Return empty object for other endpoints
          return {} as T;
        }
      } catch (parseError) {
        logger.error('Failed to parse API response:', isPrivateReporting ? '[private reporting details omitted]' : parseError);
        
        // For search endpoints, return a structured empty results object instead of throwing
        if (endpoint.includes('/search')) {
          return { 
            results: [], 
            total: 0, 
            took_ms: 0, 
            message: 'Error parsing search response'
          } as T;
        }
        
        throw new Error(`Failed to parse API response: ${parseError instanceof Error ? parseError.message : 'Unknown error'}`);
      }
    } catch (error) {
      // Clear timeout on error
      clearTimeout(timeoutId);

      // Handle timeout/abort errors specifically
      if (error instanceof Error && error.name === 'AbortError') {
        logger.error(`Request to ${endpoint} was aborted (timeout: ${timeout}ms)`);
        throw new Error(`Request timed out after ${timeout}ms. The server may be overloaded or the operation is taking too long.`);
      }

      logger.error('API request failed:', isPrivateReporting ? '[private reporting details omitted]' : error);

      // For search endpoints, return a structured empty results object instead of throwing
      if (endpoint.includes('/search')) {
        return { 
          results: [], 
          total: 0, 
          took_ms: 0, 
          message: 'Search request failed'
        } as T;
      }
      
      // For techniques endpoints, return empty results instead of throwing
      if (endpoint.includes('/techniques') || (endpoint.includes('/users') && endpoint.includes('/techniques'))) {
        
        // Handle different technique endpoints
        if (endpoint.includes('/users') && endpoint.includes('/techniques')) {
          // User techniques endpoint
          return { userTechniques: [] } as T;
        } else if (endpoint.includes('/techniques/categories')) {
          // Technique categories endpoint
          return { categories: [] } as T;
        } else if (endpoint.includes('/techniques/tags')) {
          // Technique tags endpoint
          return { tags: [] } as T;
        } else {
          // Generic techniques endpoint
          return { techniques: [] } as T;
        }
      }
      
      throw error;
    }
  }

  /**
   * Make a GET request
   */
  async get<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'GET',
    });
  }

  /**
   * Make a POST request
   */
  async post<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    const isFormData = data instanceof FormData;
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
      ...(isFormData ? { _isFormData: true } : {}),
    } as any);
  }

  /**
   * Make a PUT request
   */
  async put<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a PATCH request
   */
  async patch<T>(endpoint: string, data?: any, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * Make a DELETE request
   */
  async delete<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'DELETE',
    });
  }

  /**
   * Check if the API is reachable
   */
  async checkHealth(): Promise<boolean> {
    try {
      await this.get('/health', { requiresAuth: false });
      return true;
    } catch (error) {
      logger.error('API health check failed:', error);
      return false;
    }
  }
  
  /**
   * Test if an endpoint is available
   * Useful for checking multiple endpoint options
   */
  async testEndpoint(endpoint: string, options: RequestOptions = {}): Promise<{
    available: boolean;
    statusCode?: number;
    errorMessage?: string;
  }> {
    try {
      // Set requiresAuth to false by default for testing
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { requiresAuth = false, ...restOptions } = options;
      
      // Build URL with query parameters
      const url = this.buildUrl(endpoint, options.params);
      
      // Make a simple HEAD request to check if the endpoint exists
      const response = await fetch(url, {
        method: 'HEAD',
        headers: this.defaultHeaders,
        mode: 'cors',
        credentials: 'omit',
      });
      
      return {
        available: response.ok,
        statusCode: response.status
      };
    } catch (error) {
      return {
        available: false,
        errorMessage: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }
  
  /**
   * Discover available search endpoints
   * Tests multiple endpoint patterns to find working ones
   */
  async discoverSearchEndpoints(): Promise<string[]> {
    const endpointsToTest = [
      '/api/v1/search/users',
      '/api/search/users',
      '/search/users',
      '/users/search',
      '/search'
    ];
    
    const results = await Promise.all(
      endpointsToTest.map(async endpoint => {
        const test = await this.testEndpoint(endpoint);
        return {
          endpoint,
          available: test.available,
          statusCode: test.statusCode
        };
      })
    );
    
    // Return available endpoints
    return results
      .filter(result => result.available)
      .map(result => result.endpoint);
  }
}

// Create a singleton instance
const apiClient = new ApiClient();

export default apiClient;
